import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Oval } from "react-loader-spinner";
import { toast } from "react-toastify";

import { ChevronIcon, DownloadMenuIcon, DeleteMenuIcon } from "../common/DocumentRowIcons";
import { formatDate, getAvatarUrl, getRealFileName, getUploadUrl } from "src/Common/common";
import { downloadImage, handleError, isAccountant, isClericalOver } from "src/Common/function";
import WOService from "src/Services/API/wo.service";
import UserService from "src/Services/API/user.service";
import ConfirmDialog from "src/Common/confirmdialog";
import AttachmentImageSlider from "src/Pages/WO/Components/attachment/AttachmentImageSlider";

// Jobsite photos are WoAttachment rows with type === 1 (single-table
// inheritance shares the same table with technical documents/forms).
const JOBSITE_PHOTO_TYPE = 1;
// The /attach upload flow uses type 1 for "attachments to the work order";
// the per-file metadata.attachmentType then narrows it to a jobsite photo.
const ATTACHMENT_UPLOAD_FLOW = 1;

const isVideoFile = (fileName) =>
  !!fileName && /\.(mp4|mov|webm|avi|mkv|m4v|3gp)$/i.test(fileName);

const ServiceTicketToggle = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-[12px] p-[2px] transition-colors ${
      checked ? "bg-[#0065FD]" : "bg-[#EEF0F9]"
    } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-[16px] w-[16px] transform rounded-full bg-white shadow-[0px_1px_2px_rgba(10,12.67,18,0.06),0px_1px_3px_rgba(10,12.67,18,0.10)] transition-transform ${
        checked ? "translate-x-[16px]" : "translate-x-0"
      }`}
    />
  </button>
);

const PlayIcon = () => (
  <div className="w-[32px] h-[32px] rounded-full bg-white/20 flex items-center justify-center">
    <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 1L12 7L0 13V1Z" fill="white" />
    </svg>
  </div>
);

function JobsitePhotos({ order, onRefresh }) {
  const currentUser = UserService.getCurrentUser();
  const fileInputRef = useRef(null);
  const [expanded, setExpanded] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  // Fixed-position coordinates for the open menu. The card has overflow-hidden,
  // so the menu is portaled to the body and positioned from the button's rect,
  // flipping above the button when there isn't room below.
  const [menuPosition, setMenuPosition] = useState(null);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(null);

  const canManage = isClericalOver() || isAccountant();

  const photos = useMemo(() => {
    const attachments = order?.attachments || [];
    return attachments
      .filter((attachment) => Number(attachment?.type) === JOBSITE_PHOTO_TYPE)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [order?.attachments]);

  const getUploaderName = (photo) =>
    photo?.uploadedBy?.name ||
    photo?.uploadedByCustomerUser?.customer?.companyName ||
    photo?.uploadedByCustomerUser?.account?.name ||
    "-";

  const getDisplayName = (photo) =>
    photo.documentName || getRealFileName(photo.fileName) || photo.fileName;

  // Map image photos to the shape AttachmentImageSlider expects. Videos are not
  // part of the slider (it only renders images/PDFs) and are excluded here.
  const sliderPhotos = useMemo(
    () =>
      photos
        .filter((photo) => !isVideoFile(photo.fileName))
        .map((photo) => ({
          id: photo.id,
          fileName: photo.fileName,
          isPdf: false,
          displayName: getDisplayName(photo),
          description: photo.description || "",
          tags: photo.tags || [],
          createdBy: getUploaderName(photo),
          createdAt: photo.createdAt,
        })),
    [photos],
  );

  const uploadFiles = async (files) => {
    if (!files.length || !order?.id) return;
    setIsUploading(true);
    try {
      // Tag every uploaded file as a jobsite photo so it lands in this section
      // rather than the generic attachments list.
      const metadata = files.map(() => ({ attachmentType: JOBSITE_PHOTO_TYPE }));
      const response = await WOService.uploadFiles(files, order.id, ATTACHMENT_UPLOAD_FLOW, currentUser, metadata);
      if (response.status === 201) {
        toast(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded successfully`);
        onRefresh?.();
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelected = async (e) => {
    const input = e.target;
    const files = Array.from(input?.files || []);
    await uploadFiles(files);
    if (input) input.value = null;
  };

  const handleToggleServiceTicket = async (photo) => {
    if (togglingId) return;
    setTogglingId(photo.id);
    try {
      const response = await WOService.updateAttachment({
        id: photo.id,
        data: { includeInServiceTicket: !photo.includeInServiceTicket },
      });
      if (response.status === 200 || response.status === 201) {
        onRefresh?.();
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpen = (photo) => {
    if (!photo?.fileName) return;
    if (isVideoFile(photo.fileName)) {
      // The slider only renders images/PDFs; open videos in a new tab.
      window.open(getUploadUrl(photo.fileName), "_blank", "noopener");
      return;
    }
    const index = sliderPhotos.findIndex((attachment) => attachment.id === photo.id);
    if (index >= 0) setSliderIndex(index);
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const handleToggleMenu = (event, photo) => {
    event.stopPropagation();
    if (openMenuId === photo.id) {
      closeMenu();
      return;
    }
    const MENU_WIDTH = 180;
    // Approximate height: one item (~40px) plus the delete item when shown,
    // plus the container's vertical padding.
    const estimatedHeight = (canManage ? 80 : 40) + 8;
    const rect = event.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedHeight + 8 && rect.top > spaceBelow;
    setMenuPosition({
      top: openUp ? rect.top - estimatedHeight - 4 : rect.bottom + 4,
      // Right-align the menu to the button.
      left: Math.max(8, rect.right - MENU_WIDTH),
    });
    setOpenMenuId(photo.id);
  };

  const handleDownload = (photo) => {
    closeMenu();
    if (!photo?.fileName) return;
    downloadImage(getUploadUrl(photo.fileName), getDisplayName(photo));
  };

  const handleDelete = async () => {
    const photo = photoToDelete;
    setPhotoToDelete(null);
    if (!photo?.id || !order?.id) return;
    try {
      await WOService.deleteAttachment(order.id, photo.id, ATTACHMENT_UPLOAD_FLOW);
      toast("Photo deleted successfully");
      onRefresh?.();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div className="w-full bg-white rounded-[12px] overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3]">
      <input
        ref={fileInputRef}
        onChange={handleFileSelected}
        type="file"
        multiple
        accept="image/*,video/*"
        style={{ display: "none" }}
      />

      {/* Header (click anywhere to expand/collapse) */}
      <div
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full px-6 py-4 flex items-center justify-between bg-[#F8F9FE] border-b border-[#E5E8F3] cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
            Photos/Videos
          </span>
          <span className="min-w-[24px] px-2 py-[2px] flex items-center justify-center rounded-[16px] bg-[#EEF0F9] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
            {photos.length}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {canManage && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              disabled={isUploading}
              className={`h-[32px] px-3 rounded-[8px] bg-[#0065FD] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-white ${
                isUploading ? "cursor-not-allowed opacity-70" : "cursor-pointer"
              }`}
            >
              {isUploading ? (
                <Oval height="14" width="14" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" visible />
              ) : (
                "Upload"
              )}
            </button>
          )}
          <ChevronIcon expanded={expanded} />
        </div>
      </div>

      {/* Grid-rows 0fr->1fr animates the body height without a fixed max-height. */}
      <div
        className={`w-full grid transition-[grid-template-rows] duration-300 ease-in-out ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="w-full min-h-0 overflow-hidden">
          {photos.length === 0 ? (
            <div className="w-full px-6 py-6">
              <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                No photos or videos
              </span>
            </div>
          ) : (
            <div className="w-full p-6 flex flex-wrap content-start items-start gap-4">
              {photos.map((photo) => {
                const displayName = getDisplayName(photo);
                const isVideo = isVideoFile(photo.fileName);
                const tags = photo.tags || [];
                return (
                  <div
                    key={photo.id}
                    className="w-[258px] flex flex-col rounded-[12px] overflow-hidden border border-[#E5E8F3]"
                  >
                    {/* Thumbnail */}
                    <div
                      onClick={() => handleOpen(photo)}
                      className="relative w-full h-[160px] bg-[#F3F4F6] border-b border-[#E5E8F3] cursor-pointer overflow-hidden"
                    >
                      {isVideo ? (
                        <>
                          <video
                            src={getUploadUrl(photo.fileName)}
                            className="w-full h-full object-cover"
                            preload="metadata"
                            muted
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <PlayIcon />
                          </div>
                        </>
                      ) : (
                        <img
                          src={getUploadUrl(photo.fileName)}
                          alt={displayName}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>

                    {/* Body */}
                    <div className="flex flex-col gap-3 p-3 overflow-hidden">
                      <div className="flex flex-col gap-[2px]">
                        <div className="flex items-center gap-2">
                          <span
                            className="flex-1 min-w-0 font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#1A2332] truncate"
                            title={displayName}
                          >
                            {displayName}
                          </span>
                          <div className="relative shrink-0">
                            <button
                              type="button"
                              onClick={(e) => handleToggleMenu(e, photo)}
                              className="px-1 -mr-1 rounded-[6px] text-[#7C829A] cursor-pointer hover:bg-slate-50"
                            >
                              &#8942;
                            </button>
                            {openMenuId === photo.id &&
                              menuPosition &&
                              createPortal(
                                <>
                                  {/* Click-away overlay */}
                                  <div className="fixed inset-0 z-[1000]" onClick={closeMenu} />
                                  <div
                                    className="fixed z-[1001] w-[180px] flex flex-col gap-[2px] px-1 py-1 bg-white rounded-[12px] border border-[#E5E8F3] shadow-[0px_4px_12px_0px_rgba(10,12.67,18,0.10)]"
                                    style={{ top: menuPosition.top, left: menuPosition.left }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => handleDownload(photo)}
                                      className="w-full min-h-[36px] px-3 py-[6px] flex items-center gap-3 rounded-[8px] text-left hover:bg-[#F8F9FE]"
                                    >
                                      <DownloadMenuIcon />
                                      <span className="flex-1 font-InterMedium text-[14px] leading-[20px] text-[#1A2332]">
                                        Download
                                      </span>
                                    </button>
                                    {canManage && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          closeMenu();
                                          setPhotoToDelete(photo);
                                        }}
                                        className="w-full min-h-[36px] px-3 py-[6px] flex items-center gap-3 rounded-[8px] text-left hover:bg-[#F8F9FE]"
                                      >
                                        <DeleteMenuIcon />
                                        <span className="flex-1 font-InterMedium text-[14px] leading-[20px] text-[#EF4444]">
                                          Delete file
                                        </span>
                                      </button>
                                    )}
                                  </div>
                                </>,
                                window.document.body,
                              )}
                          </div>
                        </div>
                        {photo.description && (
                          <p className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477] line-clamp-2">
                            {photo.description}
                          </p>
                        )}
                      </div>

                      {tags.length > 0 && (
                        <div className="flex flex-wrap content-start gap-2">
                          {tags.map((tag) => (
                            <span
                              key={tag.id ?? tag.name ?? tag}
                              className="inline-block px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#0058E5]"
                            >
                              {tag.name ?? tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-2 overflow-hidden">
                        {photo.uploadedBy?.avatar ? (
                          <img
                            src={getAvatarUrl(photo.uploadedBy.avatar)}
                            alt=""
                            className="w-[16px] h-[16px] rounded-full object-cover"
                          />
                        ) : (
                          <span className="w-[16px] h-[16px] rounded-full bg-[#EEF0F9]" />
                        )}
                        <span className="font-OpenSansRegular text-[10px] leading-[14px] tracking-[0.10px] text-[#7C829A] truncate">
                          {getUploaderName(photo)}
                          {photo.createdAt ? ` • ${formatDate(photo.createdAt, "MMM D")}` : ""}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#E5E8F3]">
                        <span className="font-OpenSansSemiBold text-[10px] leading-[14px] tracking-[0.10px] capitalize text-[#7C829A]">
                          Include in Service Ticket
                        </span>
                        <ServiceTicketToggle
                          checked={!isVideo && !!photo.includeInServiceTicket}
                          disabled={isVideo || !canManage || togglingId === photo.id}
                          onChange={() => handleToggleServiceTicket(photo)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        title="DELETE PHOTO"
        description="Are you sure you want to delete this photo?"
        shown={!!photoToDelete}
        onConfirmClicked={handleDelete}
        onCancelClicked={() => setPhotoToDelete(null)}
      />

      <AttachmentImageSlider
        shown={sliderIndex !== null}
        attachments={sliderPhotos}
        activeIndex={sliderIndex ?? 0}
        onCancel={() => setSliderIndex(null)}
      />
    </div>
  );
}

export default JobsitePhotos;
